import type { TicketEventMessage } from './events';
import {
  ordinal,
  renderNotification,
  type RenderedNotification,
} from './notification-text';

interface PerTicketBucket {
  timer: ReturnType<typeof setTimeout>;
  /**
   * Most recent event that carried real semantic action data (status change,
   * comment, priority change, etc.). Used to render the flushed notification.
   * Falls through to a generic "made N changes" if nothing meaningful arrived.
   */
  lastMeaningful?: TicketEventMessage;
  commentCount: number;
  titleHint?: string;
}

interface RankBucket {
  timer: ReturnType<typeof setTimeout>;
  items: Map<number, { ticketId: number; title: string; newRank: number }>;
  lastActorName?: string;
}

function isMeaningful(msg: TicketEventMessage): boolean {
  const p = msg.payload ?? {};
  return (
    msg.type === 'ticket.comment_added' ||
    !!p.toStatus ||
    !!p.toPriority ||
    !!p.toAssigneeName ||
    !!p.toAssigneeUserId ||
    !!p.resolutionPreview ||
    !!p.blockedReasonPreview ||
    !!p.reopenReason
  );
}

/**
 * Per-ticket throttle + cross-ticket rank batcher.
 *
 *   * Same-ticket bursts of ticket.updated / ticket.comment_added inside
 *     `windowMs` collapse into a single notification rendered from the most
 *     meaningful event in the burst.
 *   * Priority-queue reorders fire one ticket.updated per touched ticket;
 *     those land in a separate cross-ticket bucket and flush as one
 *     "Priority queue reordered" notification within `rankWindowMs`.
 *
 * Discrete events (ticket.created, URGENT, deletion) are NOT buffered —
 * `ingest` returns false for them and drains the matching pending bucket
 * first so accumulated context isn't dropped.
 */
export class TicketNotificationCoalescer {
  private perTicket = new Map<number, PerTicketBucket>();
  private rank: RankBucket | null = null;

  constructor(
    private readonly windowMs: number,
    private readonly onFlush: (n: RenderedNotification, ticketId: number | null) => void,
    private readonly selfUserIdRef: { current: string | null },
    private readonly rankWindowMs = 5_000,
  ) {}

  /**
   * Returns true if the message was buffered (caller should not fire
   * immediately). Returns false for immediate events; any pending bucket for
   * the same ticket is flushed first so the immediate toast carries the
   * accumulated context.
   */
  ingest(msg: TicketEventMessage): boolean {
    const payload = msg.payload ?? {};

    // Non-ticket events (e.g. suggestion.created) bypass coalescing entirely —
    // they fire one toast each. Rare enough that batching isn't needed.
    const ticketId = msg.ticketId;
    if (ticketId === undefined) {
      return false;
    }

    // Rank events: batch across tickets into one toast.
    if (payload.newRank !== undefined) {
      this.accumulateRank(msg, ticketId, Number(payload.newRank));
      return true;
    }

    const isUrgent = payload.priority === 'URGENT';
    const isDeleted = payload.deleted === true;
    const isCreated = msg.type === 'ticket.created';
    const isBucketable =
      !isCreated &&
      !isUrgent &&
      !isDeleted &&
      (msg.type === 'ticket.updated' || msg.type === 'ticket.comment_added');

    if (!isBucketable) {
      this.flushTicket(ticketId);
      return false;
    }

    let bucket = this.perTicket.get(ticketId);
    if (!bucket) {
      bucket = {
        timer: setTimeout(() => this.flushTicket(ticketId), this.windowMs),
        commentCount: 0,
      };
      this.perTicket.set(ticketId, bucket);
    }
    if (msg.type === 'ticket.comment_added') bucket.commentCount += 1;
    const t = payload.title as string | undefined;
    if (t) bucket.titleHint = t;
    if (isMeaningful(msg)) bucket.lastMeaningful = msg;
    return true;
  }

  flushTicket(ticketId: number): void {
    const bucket = this.perTicket.get(ticketId);
    if (!bucket) return;
    this.perTicket.delete(ticketId);
    clearTimeout(bucket.timer);

    const self = this.selfUserIdRef.current;
    if (bucket.lastMeaningful) {
      this.onFlush(renderNotification(bucket.lastMeaningful, self), ticketId);
      return;
    }
    if (bucket.commentCount > 0) {
      // No comment carried preview text (rare, e.g. an old publisher) — emit a
      // generic count summary so the user still hears about it.
      this.onFlush(
        {
          title: bucket.titleHint
            ? `#${ticketId} ${bucket.titleHint}`
            : `Ticket #${ticketId}`,
          body:
            bucket.commentCount === 1
              ? '1 new comment'
              : `${bucket.commentCount} new comments`,
          path: `/tickets/${ticketId}?tab=activity`,
        },
        ticketId,
      );
      return;
    }
    // Bucket only contained suppressible/silent events — drop silently.
  }

  private accumulateRank(msg: TicketEventMessage, ticketId: number, newRank: number) {
    if (!this.rank) {
      this.rank = {
        timer: setTimeout(() => this.flushRank(), this.rankWindowMs),
        items: new Map(),
      };
    }
    const title = (msg.payload?.title as string | undefined) ?? '';
    this.rank.items.set(ticketId, {
      ticketId,
      title,
      newRank,
    });
    const actor = msg.payload?.actorName as string | undefined;
    if (actor) this.rank.lastActorName = actor;
  }

  private flushRank() {
    const b = this.rank;
    this.rank = null;
    if (!b) return;
    clearTimeout(b.timer);
    if (b.items.size === 0) return;

    const ordered = [...b.items.values()].sort((a, b2) => a.newRank - b2.newRank);
    const title =
      b.items.size === 1
        ? `Priority queue: #${ordered[0]!.ticketId}`
        : 'Priority queue reordered';
    const lines = ordered.map((i) => {
      const titleSnippet = i.title ? ` ${truncate(i.title, 40)}` : '';
      return `#${i.ticketId} → ${ordinal(i.newRank)}${titleSnippet}`;
    });
    const actorPrefix = b.lastActorName ? `${b.lastActorName} reordered:\n` : '';
    const body = actorPrefix + lines.join('\n');

    this.onFlush({ title, body, path: '/queue' }, null);
  }

  dispose(): void {
    for (const b of this.perTicket.values()) clearTimeout(b.timer);
    this.perTicket.clear();
    if (this.rank) {
      clearTimeout(this.rank.timer);
      this.rank = null;
    }
  }
}

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max - 1) + '…' : s;
}
